/**
 * Hook de la emisión global DOOH (TG-59): lee la playlist SMIL activa.
 *
 * El backend responde 404 `DOOH_BROADCAST_EMPTY` cuando todavía no hay emisión; eso
 * NO es un error de carga: la UI lo trata como estado vacío (acción sugerida:
 * componer y publicar).
 */
import { useCallback, useEffect, useState } from 'react';
import type { DoohBroadcastResponse } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { doohApi } from '../api/doohApi';

export interface UseDoohBroadcastResult {
  broadcast: DoohBroadcastResponse | null;
  loading: boolean;
  error: string | null;
  /** `true` si no hay emisión activa (`DOOH_BROADCAST_EMPTY`). */
  empty: boolean;
  refresh: () => void;
}

const EMPTY_CODE = 'DOOH_BROADCAST_EMPTY';

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar la emisión global.';
}

function isEmptyBroadcast(error: unknown): boolean {
  return error instanceof ApiError && (error.code === EMPTY_CODE || error.status === 404);
}

export function useDoohBroadcast(): UseDoohBroadcastResult {
  const [broadcast, setBroadcast] = useState<DoohBroadcastResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [empty, setEmpty] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    doohApi
      .getBroadcast()
      .then((response) => {
        if (cancelled) return;
        setBroadcast(response);
        setEmpty(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isEmptyBroadcast(err)) {
          setBroadcast(null);
          setEmpty(true);
        } else {
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { broadcast, loading, error, empty, refresh };
}
